import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { ApiError, adminApi, type AgreementScope } from '../lib/api.js';
import {
  audienceBody,
  audienceLabel,
  refusedField,
  type AudienceKind,
} from '../lib/agreement-audience.js';
import type { AgreementKind } from '../lib/agreement-public.js';
import { describe, useAsync, useAction } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { count } from '../lib/format.js';
import {
  ChipBar,
  FilterPills,
  Menu,
  MenuItem,
  PrimaryButton,
  Screen,
  inputStyle,
} from '../components/Screen.js';
import { useCan } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  Num,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { AgreementDetail } from './AgreementDetail.js';

const PAGE = 50;

/**
 * Agreements, versions, and the record of who accepted what.
 *
 * ── This screen returned 1,148 rows, and that was auth#55 ────────────
 *
 * The listing was unbounded. On dev it returns over a thousand agreements, each
 * carrying a version subquery, and rendering all of them made a screenshot
 * attempt time out with the renderer unresponsive — while the create action sat
 * in a disclosure BELOW all of them.
 *
 * Both halves are fixed here: the server pages and filters (see its
 * agreements-admin.ts), and the primary action is top-right in a modal. A cap
 * alone would not have been enough — truncating at 50 answers "find the one I
 * am looking for" only for whoever sorts first alphabetically, which is why the
 * scope pills and the chip search arrive with it.
 */
export function Agreements({ id }: { id?: string }) {
  /* Keyed, so moving between two agreements remounts rather than showing the
     previous one's versions under a new heading while the fetch runs. */
  return id ? <AgreementDetail key={id} id={id} /> : <AgreementList />;
}

function AgreementList() {
  const [scope, setScope] = useState<AgreementScope>('all');
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const mayWrite = useCan('agreement:write');
  const search = useChips([], () => setOffset(0));

  const { state, reload } = useAsync(
    () => adminApi.agreements({ scope, terms: search.active, limit: PAGE, offset }),
    [search.key, scope, offset],
  );

  return (
    <Screen
      filters={
        <FilterPills
          label="Filter by scope"
          current={scope}
          onSelect={(v) => {
            setScope(v);
            setOffset(0);
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'org', label: 'Org-wide' },
            { value: 'app', label: 'App-scoped' },
            { value: 'group', label: 'Group-scoped' },
            { value: 'retired', label: 'Retired' },
          ]}
        />
      }
      actions={
        /* HIDDEN, not disabled, without `agreement:write` (Jon, 2026-09-16,
           org-admin#134). Support Admin reads agreements to answer "what does
           this person owe" and never writes one, so a greyed button with a
           tooltip was a control that could only ever say no. Optimistic while
           capabilities load, like every other gate here. */
        mayWrite ? (
          <PrimaryButton onClick={() => setAdding(true)}>+ New document</PrimaryButton>
        ) : undefined
      }
      chips={
        <ChipBar
          search={search}
          placeholder="Search name, slug or app…"
          appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
        />
      }
    >
      <Panel title="Documents">
        <AsyncBoundary state={state}>
          {(d) => (
            <>
              {d.agreements.length === 0 ? (
                <EmptyState
                  title={
                    search.active.length > 0 || scope !== 'all'
                      ? 'No documents match'
                      : 'Nothing published yet'
                  }
                  suggestion={
                    search.active.length > 0 || scope !== 'all'
                      ? 'Try a different term, or switch the scope back to All. “All” includes retired ones.'
                      : mayWrite
                        ? 'Create one, then publish its first version to put it in front of people.'
                        : 'Documents appear here once Legal publishes one.'
                  }
                />
              ) : (
                <Table columns={['Document', 'Scope', 'In force', 'Versions', 'Status', '']}>
                  {d.agreements.map((a) => (
                    <tr key={a.id} className="rw-rowhover" style={{ opacity: a.active ? 1 : 0.55 }}>
                      <Td>
                        {/* An anchor since the agreement became a screen — same
                            reasoning as the Users list. */}
                        <a href={`#/legal/${encodeURIComponent(a.id)}`} style={rowLink}>
                          {a.name}
                        </a>
                        <div style={{ color: C.muted, fontSize: '.78rem' }}>{a.slug}</div>
                      </Td>
                      <Td>
                        {/* Never blank: that would read as missing data on the
                            field that decides who owes it. */}
                        {a.kind === 'notice' ? <Badge>notice</Badge> : <Badge>{audienceLabel(a)}</Badge>}
                      </Td>
                      <Td>
                        {a.inForce ? (
                          <>
                            <Badge tone="good">{a.inForce.version}</Badge>
                            <div style={{ color: C.muted, fontSize: '.78rem' }}>
                              since <When iso={a.inForce.effectiveAt} />
                            </div>
                          </>
                        ) : (
                          /* Versions can exist while none is in force — a future
                             effective date, or one never activated. "None" and
                             "0 versions" are different problems. */
                          <Badge tone="warn">none in force</Badge>
                        )}
                      </Td>
                      <Td>
                        <Num value={a.versionCount} />
                      </Td>
                      <Td>
                        {a.active ? <Badge tone="good">active</Badge> : <Badge>retired</Badge>}
                      </Td>
                      <Td>
                        <Menu label={`Actions for ${a.name}`}>
                          {(close) => (
                            <MenuItem
                              href={`#/legal/${encodeURIComponent(a.id)}`}
                              onClick={close}
                            >
                              Open details
                            </MenuItem>
                          )}
                        </Menu>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}

              <div style={pager}>
                <span style={{ fontSize: '.82rem', color: C.muted }}>
                  {count(d.total === 0 ? 0 : offset + 1)}–{count(offset + d.agreements.length)} of{' '}
                  {count(d.total)}
                </span>
                <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
                  <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                    Previous
                  </Button>
                  <Button
                    disabled={offset + d.agreements.length >= d.total}
                    onClick={() => setOffset(offset + PAGE)}
                  >
                    Next
                  </Button>
                </span>
              </div>
            </>
          )}
        </AsyncBoundary>
      </Panel>

      {adding ? (
        <NewAgreement
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </Screen>
  );
}

function NewAgreement({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [docKind, setDocKind] = useState<AgreementKind>('agreement');
  const [kind, setKind] = useState<AudienceKind>('org');
  const [appSlug, setAppSlug] = useState('');
  const [groupSlug, setGroupSlug] = useState('');
  /** A refusal auth pinned to one field, shown under that field. */
  const [refused, setRefused] = useState<{ field: 'slug' | 'audience'; message: string } | null>(null);
  const action = useAction();
  /* A notice is owed by nobody, so it has no audience to send: org-wide is
     only the shape of "no app, no group" on the row. */
  const audience = docKind === 'notice' ? {} : audienceBody(kind, { appSlug, groupSlug });

  return (
    <Modal
      title="New document"
      onClose={onClose}
      width={500}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !slug.trim() || !name.trim() || audience === null}
            onClick={() =>
              void action
                .run(async () => {
                  setRefused(null);
                  try {
                    await adminApi.createAgreement({
                      slug: slug.trim(),
                      name: name.trim(),
                      kind: docKind,
                      ...audience,
                    });
                  } catch (e) {
                    const field = e instanceof ApiError ? refusedField(e.code) : null;
                    if (field) setRefused({ field, message: describe(e) });
                    throw e;
                  }
                  return docKind === 'notice'
                    ? 'Created. It appears on ridgewill.com once it has a web address and a published version.'
                    : 'Created. It owes nobody anything until a version is published.';
                })
                /* Only on success: closing on a refusal took the error with it,
                   so a taken slug read as a create that silently did nothing. */
                .then((ok) => ok && onCreated())
            }
          >
            {action.running ? 'Creating…' : 'Create'}
          </Button>
        </ModalActions>
      }
    >
      {/* Once, under the field it is about, when auth said which. */}
      <OutcomeNote outcome={refused ? null : action.outcome} />
      <Field
        label="Slug"
        hint="Permanent. It is matched by exact value in every user's pending list, so it cannot be changed afterwards."
        error={refused?.field === 'slug' ? refused.message : undefined}
      >
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          style={inputStyle}
          placeholder="org-tos"
        />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </Field>

      {/* First, because it decides whether the audience question exists. */}
      <fieldset style={audienceBox}>
        <legend style={legend}>Kind</legend>
        <div style={{ ...radios, flexDirection: 'column', gap: '.4rem' }} role="radiogroup" aria-label="Kind">
          {KINDS.map((o) => (
            <label key={o.kind}>
              <input
                type="radio"
                name="kind"
                checked={docKind === o.kind}
                onChange={() => {
                  setDocKind(o.kind);
                  setRefused(null);
                }}
              />{' '}
              <strong>{o.label}</strong>: {o.short}
            </label>
          ))}
        </div>
        <span style={hintText}>
          {KINDS.find((o) => o.kind === docKind)?.hint} Set now or never: the kind cannot be
          changed after it is created.
        </span>
      </fieldset>

      {/* A fieldset, not a Field: Field is a <label>, and three radios inside
          one label all answer to a click on any of them. */}
      {docKind === 'agreement' ? (
        <fieldset style={audienceBox}>
          <legend style={legend}>Who owes it</legend>
          <div style={radios} role="radiogroup" aria-label="Who owes it">
            {AUDIENCES.map((o) => (
              <label key={o.kind}>
                <input
                  type="radio"
                  name="audience"
                  checked={kind === o.kind}
                  onChange={() => {
                    setKind(o.kind);
                    setRefused(null);
                  }}
                />{' '}
                {o.label}
              </label>
            ))}
          </div>
          {kind === 'app' ? <AppPicker value={appSlug} onChange={setAppSlug} /> : null}
          {kind === 'group' ? <GroupPicker value={groupSlug} onChange={setGroupSlug} /> : null}
          {refused?.field === 'audience' ? (
            <span style={{ ...hintText, color: C.dangerText }}>{refused.message}</span>
          ) : (
            <span style={hintText}>
              {AUDIENCES.find((o) => o.kind === kind)?.hint} Set now or never: an agreement&rsquo;s
              audience cannot be changed after it is created.
            </span>
          )}
        </fieldset>
      ) : null}
    </Modal>
  );
}

const KINDS: { kind: AgreementKind; label: string; short: string; hint: string }[] = [
  {
    kind: 'agreement',
    label: 'Agreement',
    short: 'people accept it',
    hint: 'Owed by the audience below: publishing a version puts it in front of them to accept.',
  },
  {
    kind: 'notice',
    label: 'Notice',
    short: 'published on the website only, never owed',
    hint: 'Versioned and shown on ridgewill.com, but nobody is ever asked to accept it and it cannot become an agreement later.',
  },
];

const AUDIENCES: { kind: AudienceKind; label: string; hint: string }[] = [
  { kind: 'org', label: 'Everybody', hint: 'Org-wide: owed by every account.' },
  {
    kind: 'app',
    label: 'One application',
    hint: 'Owed only by people holding a role that grants that app, and stops being owed at all if the app is retired.',
  },
  {
    kind: 'group',
    label: 'One security group',
    hint: 'Owed only by the group’s members, whichever app asks. The group cannot be deleted while an agreement names it.',
  },
];

/**
 * The application list, or a typed slug when this account cannot read it.
 *
 * Legal Admin, the role that creates agreements, holds neither
 * `application:read` nor `securityGroup:read`, so for the person most likely to
 * be here the list is a refusal. A typed slug is safe because auth checks it
 * before creating anything (UNKNOWN_APPLICATION, UNKNOWN_GROUP).
 */
function AppPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { state } = useAsync(() => adminApi.applications());
  if (state.status === 'failed') return <TypedSlug what="applications" value={value} onChange={onChange} />;
  return (
    <AsyncBoundary state={state}>
      {(d) => (
        <Select
          block
          label="Application"
          value={value}
          onChange={onChange}
          options={[
            { value: '', label: 'Choose an application…' },
            ...d.applications
              .filter((a) => a.active)
              .map((a) => ({ value: a.slug, label: `${a.name} (${a.slug})` })),
          ]}
        />
      )}
    </AsyncBoundary>
  );
}

function GroupPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { state } = useAsync(() => adminApi.securityGroups());
  if (state.status === 'failed') return <TypedSlug what="security groups" value={value} onChange={onChange} />;
  return (
    <AsyncBoundary state={state}>
      {(d) => (
        <Select
          block
          label="Security group"
          value={value}
          onChange={onChange}
          options={[
            { value: '', label: 'Choose a security group…' },
            ...d.groups.map((g) => ({
              value: g.slug,
              label: `${g.name} (${g.slug}, ${count(g.members)} ${g.members === 1 ? 'member' : 'members'})`,
            })),
          ]}
        />
      )}
    </AsyncBoundary>
  );
}

function TypedSlug({
  what,
  value,
  onChange,
}: {
  what: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={`Slug of one of the ${what}`}
        placeholder="slug"
      />
      <span style={hintText}>
        This account cannot list {what}, so type the slug. The auth service refuses one that does
        not exist before anything is created.
      </span>
    </>
  );
}

const audienceBox: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '.6rem .9rem .8rem',
  margin: 0,
  display: 'grid',
  gap: '.6rem',
};
const legend: React.CSSProperties = { fontWeight: 600, color: C.textMid, fontSize: '.85rem', padding: '0 .3rem' };
const radios: React.CSSProperties = { display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '.88rem' };
const hintText: React.CSSProperties = { color: C.muted, fontSize: '.78rem', lineHeight: 1.4 };

const rowLink = {
  fontWeight: 600,
  color: C.brand,
  textDecoration: 'none',
};

const pager = {
  display: 'flex',
  alignItems: 'center',
  gap: '.75rem',
  padding: '.7rem 1.2rem',
  borderTop: `1px solid ${C.border}`,
};
